import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
} from "@mui/material";
import instance from "../Axios/axiosConfig";
import BuyModal from "../components/BuyModal/BuyModal";

const PreviewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await instance.get(`/publish/preview/${token}`);
        setContent(response.data);
        setIsLoading(false);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to load preview content"
        );
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [token]);

  // Load Adsterra ad script
  useEffect(() => {
    if (!content) return;

    const script1 = document.createElement("script");
    script1.type = "text/javascript";
    script1.innerHTML = `
      atOptions = {
        'key' : 'be7dedbe5d1d341393c334ba9657d8a2',
        'format' : 'iframe',
        'height' : 90,
        'width' : 728,
        'params' : {}
      };
    `;

    const script2 = document.createElement("script");
    script2.type = "text/javascript";
    script2.src = "//www.highperformanceformat.com/be7dedbe5d1d341393c334ba9657d8a2/invoke.js";

    const adContainer = document.getElementById("adsterra-preview-ad");
    if (adContainer) {
      adContainer.appendChild(script1);
      adContainer.appendChild(script2);
    }

    return () => {
      // Cleanup scripts on unmount
      if (adContainer) {
        adContainer.innerHTML = "";
      }
    };
  }, [content]);

  // Auto-refresh URL every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      // Redirect back to LivePage after 5 minutes
      navigate("/livePage");
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null) return 300; // 5 minutes in seconds
        if (prev <= 1) {
          navigate("/livePage");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    if (!seconds) return "5:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: { xs: "95%", sm: "90%", md: "80%" }, mx: "auto", mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: { xs: "95%", sm: "90%", md: "80%" }, mx: "auto", mt: 4 }}>
      {/* Warning Banner */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Preview Mode</AlertTitle>
        This is a preview of your pending content. The link will expire in{" "}
        <strong>{formatTime(timeRemaining)}</strong>. Content below is partially
        blurred for preview purposes.
      </Alert>

      {/* Title */}
      {content?.title && (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
          {content.title}
        </Typography>
      )}

      {/* Content Container with Blur Effect */}
      <Box
        sx={{
          position: "relative",
          minHeight: "400px",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Top 50% - Visible */}
        <Box
          sx={{
            height: "50%",
            minHeight: "200px",
            padding: 3,
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{
            __html: content?.storedHtmlContent
              ? JSON.parse(content.storedHtmlContent)
              : "",
          }}
        />

        {/* Advertisement */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 2,
            backgroundColor: "#f9f9f9",
            borderTop: "1px solid #e0e0e0",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <div id="adsterra-preview-ad"></div>
        </Box>

        {/* Bottom 50% - Blurred */}
        <Box
          sx={{
            height: "50%",
            minHeight: "200px",
            padding: 3,
            filter: "blur(8px)",
            WebkitFilter: "blur(8px)",
            pointerEvents: "none",
            userSelect: "none",
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{
            __html: content?.storedHtmlContent
              ? JSON.parse(content.storedHtmlContent)
              : "",
          }}
        />

        {/* Overlay Message */}
        <Box
          sx={{
            position: "absolute",
            bottom: "25%",
            left: "50%",
            transform: "translate(-50%, 50%)",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            color: "#000",
            padding: 3,
            borderRadius: 2,
            textAlign: "center",
            zIndex: 10,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            🔒 Preview Only
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#555" }}>
            Purchase premium to publish and view full content or wait for 2-5 days.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => setOpenModal(true)}
            sx={{ fontWeight: "bold" }}
          >
            Buy Now for 1$
          </Button>
        </Box>
      </Box>

      {/* Info Box */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
        <Typography variant="body2" sx={{ color: "#d32f2f" }}>
          <strong>Note:</strong> This preview allows you to see your content
          before it goes live. The URL changes every 5 minutes for security
          purposes. To publish your content and make it fully accessible,
          please complete the payment process or wait for 2-5 days. Check the Live Page for updates.
        </Typography>
      </Box>

      <BuyModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        post={content}
      />
    </Box>
  );
};

export default PreviewPage;
